import * as d3 from "d3";

const parseDate = d3.timeParse("%m/%d/%Y");

export const combineVectorSetVector = (data) => {
  return data.reduce((a, b) => {
    const vectorSet = b.VectorSet.trim();
    const vector = b.Vector.trim();
    vectorSet in a
      ? vector in a[vectorSet]
        ? a[vectorSet][vector].push(b)
        : (a[vectorSet][vector] = [b])
      : (a[vectorSet] = { [vector]: [b] });

    return a;
  }, {});
};

export const includeAndExcludeVectorSets = (
  filter,
  include = [],
  exclude = [],
  data,
  includefunction,
  excludefunction
) => {
  if (data && includefunction) {
    const includeObject = {};
    data
      .filter((item) => {
        return includefunction(item);
      })
      .forEach((d) => {
        includeObject[d.VectorSet] = {};
      });

    let includeArray = Object.keys(includeObject);
    include = Array.from(new Set(include.concat(includeArray)));
  }
  if (data && excludefunction) {
    const excludeObject = {};
    data
      .filter((item) => {
        return excludefunction(item);
      })
      .forEach((d) => {
        excludeObject[d.VectorSet] = {};
      });
    let excludeArray = Object.keys(excludeObject);
    exclude = Array.from(new Set(exclude.concat(excludeArray)));
  }

  return {
    ...filter,
    VectorSet: [...filter.VectorSet, ...include].filter(
      (el) => !exclude.includes(el)
    ),
  };
};

export const isInRange = (x, r1, r2) => {
  return (x - r1) * (x - r2) <= 0;
};

export const applyOverrides = (config, override) =>
  [config, override].reduce(
    (r, o) =>
      Object.entries(o).reduce(
        (q, [k, v]) => ({
          ...q,
          [k]: v && typeof v === "object" ? applyOverrides(q[k] || {}, v) : v,
        }),
        r
      ),
    {}
  );

export const onlyUnique = (value, index, array) => {
  return array.indexOf(value) === index && value !== null && value !== "";
};

export const getFilteredData = (
  data,
  activeLine,
  filter,
  colourMapping,
  valueFilter,
  ignoreVectorsInVectorSetFilter,
  ignoreVecLevelFilter,
  ignoreVecEventsFilter,
  ignoreVecDatesFilter
) => {
  const linedata = [];
  filter.VectorSet.filter((f) => f !== "All").forEach((vSet) => {
    const stroke =
      vSet.slice(0, 2) === "vs"
        ? +vSet.slice(2) < 21
          ? colourMapping["vs1 thru vs20"].stroke
          : +vSet.slice(2) < 61
          ? colourMapping["vs21 thru vs60"].stroke
          : colourMapping["vs61 thru vs325"].stroke
        : colourMapping[vSet].stroke;

    const strokeWidth =
      vSet.slice(0, 2) === "vs"
        ? +vSet.slice(2) < 21
          ? colourMapping["vs1 thru vs20"].strokeWidth
          : +vSet.slice(2) < 61
          ? colourMapping["vs21 thru vs60"].strokeWidth
          : colourMapping["vs61 thru vs325"].strokeWidth
        : colourMapping[vSet].strokeWidth;

    Object.keys(data[vSet]).forEach((vec) => {
      const name = vSet + "|" + vec;
      const dataItem = data[vSet][vec];
      let lastVStrength = "";
      const newitem = {
        name: name,
        vSet: vSet,
        vec: vec,
        values: dataItem
          .filter((d) => {
            return (
              d.VectorSet === vSet &&
              d.Vector.trim() === vec.trim() &&
              !Number.isNaN(d.Px)   &&
              checkVecLevel(
                filter,
                ignoreVectorsInVectorSetFilter,
                ignoreVecLevelFilter,
                d
              ) &&
              checkVecEvents(
                filter,
                ignoreVectorsInVectorSetFilter,
                ignoreVecEventsFilter,
                d
              ) &&
              checkVecDates(
                filter,
                ignoreVectorsInVectorSetFilter,
                ignoreVecDatesFilter,
                d
              ) &&
              (valueFilter ? valueFilter(d) : true)
            );
          })
          .map((item) => {
            lastVStrength = item["V-Strength"];
            return {
              date: parseDate(item.Date),
              px: item.Px,
              vectorSet: item.VectorSet,
              vecLevel: item.VecLevel,
              vecDates: item.VecDates,
              vecEvents: item.VecEvents,
            };
          })
          .sort((a, b) => {
            return a.date > b.date ? 1 : a.date < b.date ? -1 : 0;
          }),
        stroke: stroke,
        strokeWidth: strokeWidth,
        vStrength: lastVStrength,
      };
      if (newitem.values.length > 0) {
        linedata.push(newitem);
      }
    });
  });
  //render vs VectorSets underneath
  const activevSet = activeLine.respond ? activeLine.name.split("|")[0] : "";

  const vecs = linedata
    .filter(
      (item) => item.vSet !== activevSet && item.vSet.slice(0, 2) === "vs"
    )
    .sort((a, b) => b.vSet.localeCompare(a.vSet, "en", { numeric: true }));
  const price = linedata
    .filter(
      (item) => item.vSet !== activevSet && item.vSet.slice(0, 2) !== "vs"
    )
    .sort((a, b) =>
      a.vStrength > b.vStrength ? 1 : a.vStrength < b.vStrength ? -1 : 0
    );
  const active = linedata
    .filter((item) => item.vSet === activevSet)
    .map((item) => {
      return {
        ...item,
        stroke: activeLine.style.stroke ? activeLine.style.stroke : item.stroke,
        strokeWidth: activeLine.style.strokeWidth
          ? activeLine.style.strokeWidth
          : item.strokeWidth,
        vStrength: activeLine.style.opacityMapping
          ? activeLine.style.opacityMapping
          : item.vStrength,
      };
    });

  const activeBackground = activeLine.backgroundStyle
    ? linedata
        .filter((item) => item.vSet === activevSet)
        .map((item) => {
          return {
            ...item,
            stroke: activeLine.backgroundStyle.stroke
              ? activeLine.backgroundStyle.stroke
              : item.stroke,
            strokeWidth: activeLine.backgroundStyle.strokeWidth
              ? activeLine.backgroundStyle.strokeWidth
              : item.strokeWidth,
            vStrength: activeLine.backgroundStyle.opacityMapping
              ? activeLine.backgroundStyle.opacityMapping
              : item.vStrength,
          };
        })
    : [];

  return [...vecs, ...price, ...activeBackground, ...active];
};

const checkVecLevel = (
  filter,
  ignoreVectorsInVectorSetFilter,
  ignoreVecLevelFilter,
  d
) => {
  return (
    filter.VecLevel.indexOf(
      d.VecLevel === null ? "" : d.VecLevel.toString()
    ) !== -1 ||
    (ignoreVectorsInVectorSetFilter && d.VectorSet.slice(0, 2) !== "vs") ||
    ignoreVecLevelFilter
  );
};

const checkVecEvents = (
  filter,
  ignoreVectorsInVectorSetFilter,
  ignoreVecEventsFilter,
  d
) => {
  return (
    filter.VecEvents.indexOf(d.VecEvents) !== -1 ||
    (ignoreVectorsInVectorSetFilter && d.VectorSet.slice(0, 2) !== "vs") ||
    ignoreVecEventsFilter
  );
};

const checkVecDates = (
  filter,
  ignoreVectorsInVectorSetFilter,
  ignoreVecDatesFilter,
  d
) => {
  return (
    filter.VecDates.indexOf(d.VecDates) !== -1 ||
    (ignoreVectorsInVectorSetFilter && d.VectorSet.slice(0, 2) !== "vs") ||
    ignoreVecDatesFilter
  );
};
